import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formMappings } from "../../../config/formMappings";

const LINE_COLOR = "#1b3356";

export default function AgeDistributionGraph() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const ageFieldKey = formMapping?.fieldMappings?.Age || "i6b3v3";

  // Calculate age distribution by individual age
  const ageDistribution = entries.reduce((acc, entry) => {
    // First, try to find registrants in the mapped "Registrants" key
    if (entry.meta.Registrants) {
      Object.values(entry.meta.Registrants)
        .filter((registrant) => registrant && (registrant as any)[ageFieldKey])
        .forEach((registrant) => {
          const age = parseInt((registrant as any)[ageFieldKey], 10);
          if (!isNaN(age)) {
            acc[age] = (acc[age] || 0) + 1;
          }
        });
    } else {
      // Fallback: look for registrants in keys starting with "9v3kh" (registrants container)
      const registrantsKey = Object.keys(entry.meta).find(
        (key) => key.startsWith("9v3kh") && typeof entry.meta[key] === "object"
      );

      if (registrantsKey) {
        const registrantsContainer = entry.meta[registrantsKey] as any;
        if (registrantsContainer) {
          Object.values(registrantsContainer)
            .filter(
              (item: any) =>
                item &&
                typeof item === "object" &&
                item[ageFieldKey] &&
                item[ageFieldKey] !== "form"
            )
            .forEach((registrant: any) => {
              const age = parseInt(registrant[ageFieldKey], 10);
              if (!isNaN(age)) {
                acc[age] = (acc[age] || 0) + 1;
              }
            });
        }
      }
    }
    return acc;
  }, {} as { [age: number]: number });

  const ageData = Object.entries(ageDistribution).map(([age, count]) => ({
    name: age,
    count: count,
  }));

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 col-span-2">
      <h1 className="text-xl text-primary text-center">Age Distribution</h1>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart width={600} height={300} data={ageData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            label={{ value: "Age", position: "insideBottom", offset: -5 }}
          />
          <YAxis
            label={{ value: "Count", angle: -90, position: "insideLeft" }}
          />
          <Tooltip />
          <Legend />
          <Line
            type="linear"
            dataKey="count"
            stroke={LINE_COLOR}
            dot={{ stroke: LINE_COLOR, strokeWidth: 2, r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
